import React, { useState, useEffect } from "react";
import { Search, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const TypewriterText = ({ text, delay, speed = 30, className, onComplete }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      if (currentIndex < text.length) {
        const timer = setTimeout(() => {
          setDisplayedText(text.slice(0, currentIndex + 1));
          setCurrentIndex(currentIndex + 1);
        }, speed);
        return () => clearTimeout(timer);
      } else if (currentIndex === text.length && onComplete) {
        onComplete();
      }
    }, delay);

    return () => clearTimeout(startTimer);
  }, [currentIndex, text, delay, speed, onComplete]);

  useEffect(() => {
    // Reset animation
    const resetTimer = setTimeout(() => {
      setDisplayedText("");
      setCurrentIndex(0);
    }, delay + text.length * speed + 5000);

    return () => clearTimeout(resetTimer);
  }, [delay, text.length, speed]);

  return (
    <span className={className}>
      {displayedText}
      {currentIndex < text.length && (
        <motion.span
          className="inline-block w-0.5 h-4 bg-green-500 ml-0.5"
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
      )}
    </span>
  );
};

const AutomatedContentPipeline = ({ onGetStarted }) => {
  const [showContent, setShowContent] = useState(true);

  // Competitor URLs for animation
  const competitorUrls = [
    { url: "competitor1.com/blog/seo-tips", status: "Tracked" },
    { url: "competitor2.com/content/marketing-guide", status: "Analyzing" },
    { url: "competitor3.com/articles/growth-hacks", status: "Tracked" },
    { url: "competitor1.com/blog/link-building", status: "New" },
    { url: "competitor4.com/guides/content-strategy", status: "Tracked" },
    { url: "competitor2.com/posts/keyword-research", status: "Analyzing" },
  ];

  // Blog content generation steps
  const blogContent = [
    { type: "heading", text: "10 Proven SEO Strategies to Boost Your Rankings", delay: 0 },
    { 
      type: "paragraph", 
      delay: 1400,
      lines: [
        { width: "100%", delay: 1400 },
        { width: "95%", delay: 1600 },
        { width: "88%", delay: 1800 },
      ]
    },
    { 
      type: "point", 
      delay: 2200,
      lines: [{ width: "85%", delay: 2200 }]
    },
    { 
      type: "point", 
      delay: 2800,
      lines: [{ width: "90%", delay: 2800 }]
    },
  ];

  // Reset animation cycle
  useEffect(() => {
    const cycleTimer = setInterval(() => {
      setShowContent(false);
      setTimeout(() => setShowContent(true), 100);
    }, 6000); // Reset every 6 seconds

    return () => clearInterval(cycleTimer);
  }, []);
  return (
    <section className="py-10 md:py-10 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-16">
          <div className="lg:max-w-2xl">
            <div className="mb-6">
              <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">
                Competitor Analysis & Publishing
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Stay ahead of{" "}
              <span className="text-green-500">every competitor</span>
              {" "}move
            </h2>
          </div>

          <div className="lg:max-w-md lg:pt-12">
            <p className="text-gray-600 text-lg mb-6">
              We handle the SEO heavy lifting. Relax while we create daily ranking content to keep you ahead of the competition.
            </p>
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-full hover:bg-green-600 transition-colors"
            >
              Start for Free
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Three Cards Section */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1: Track Competitors */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area with Animated URLs */}
            <div className="bg-gray-100 p-8 flex items-center justify-center min-h-[280px] relative overflow-hidden">
              {/* Magnifying Glass Container */}
              <div className="relative">
                {/* URLs scrolling vertically */}
                <div className="w-[280px] h-[240px] overflow-hidden relative bg-white rounded-xl shadow-lg border-2 border-green-500">
                  <motion.div
                    className="py-4"
                    animate={{
                      y: [0, -300],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    {[...competitorUrls, ...competitorUrls].map((item, index) => (
                      <motion.div
                        key={index}
                        className="px-4 py-3 mx-4 mb-2 bg-gray-50 rounded-lg border border-gray-200"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.2 }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                          <div className="text-xs font-mono text-gray-700 truncate flex-1">
                            {item.url}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500">Just now</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            item.status === 'New' 
                              ? 'bg-green-100 text-green-700' 
                              : item.status === 'Analyzing'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                  
                  {/* Gradient overlays for smooth scroll effect */}
                  <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                </div>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Track competitor content
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We monitor your competitors' websites 24/7. Discover what content they publish, identify keyword opportunities, and stay ahead of every move.
              </p>
            </div>
          </div>

          {/* Card 2: Generate Strategic Content */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area - AI Writing Animation */}
            <div className="bg-green-50 p-8 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[280px] h-[240px] bg-white rounded-xl shadow-lg border-2 border-green-500 p-4 overflow-hidden relative">
                {/* AI Writing Indicator */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <div className="flex gap-1">
                    <motion.div
                      className="w-1.5 h-1.5 bg-green-500 rounded-full"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div
                      className="w-1.5 h-1.5 bg-green-500 rounded-full"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                    />
                    <motion.div
                      className="w-1.5 h-1.5 bg-green-500 rounded-full"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                    />
                  </div>
                  <span className="text-[10px] font-semibold text-green-500">AI Writing...</span>
                </div>

                {/* Animated Blog Content */}
                {showContent && (
                  <div className="space-y-3 text-left">
                    {blogContent.map((item, index) => (
                      <div key={index}>
                        {item.type === "heading" && (
                          <h3 className="text-xs font-bold text-gray-900 leading-tight mb-2">
                            <TypewriterText 
                              text={item.text} 
                              delay={item.delay} 
                              speed={25}
                            />
                          </h3>
                        )}
                        {item.type === "paragraph" && (
                          <div className="space-y-1.5 mb-3">
                            {item.lines.map((line, lineIndex) => (
                              <motion.div
                                key={lineIndex}
                                className="h-2 bg-gray-400 rounded"
                                initial={{ width: 0 }}
                                animate={{ width: line.width }}
                                transition={{
                                  delay: (line.delay) / 1000,
                                  duration: 0.3,
                                  ease: "easeOut"
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {item.type === "point" && (
                          <div className="flex gap-2 items-start mb-2">
                            <motion.span 
                              className="text-green-500 text-xs mt-0.5"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: (item.delay) / 1000 }}
                            >
                              •
                            </motion.span>
                            <div className="flex-1">
                              {item.lines.map((line, lineIndex) => (
                                <motion.div
                                  key={lineIndex}
                                  className="h-1.5 bg-gray-400 rounded"
                                  initial={{ width: 0 }}
                                  animate={{ width: line.width }}
                                  transition={{
                                    delay: (line.delay) / 1000,
                                    duration: 0.3,
                                    ease: "easeOut"
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Generate strategic content
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Our AI analyzes competitor articles and creates superior content. Each piece is longer, more comprehensive, and optimized to outrank the competition.
              </p>
            </div>
          </div>

          {/* Card 3: Auto Publish */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area - Integration Logos */}
            <div className="bg-green-50 p-8 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[280px]">
                
                
                {/* Integration Logos Grid */}
                <div className="bg-white rounded-xl shadow-lg border-2 border-green-500 p-6">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* WordPress */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/wordpress-icon.svg" 
                        alt="WordPress" 
                        className="w-8 h-8"
                      />
                    </motion.div>

                    {/* Webflow */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/webflow-icon.svg" 
                        alt="Webflow" 
                        className="w-8 h-8"
                      />
                    </motion.div>

                    {/* Notion */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/notion-icon.svg" 
                        alt="Notion" 
                        className="w-8 h-8"
                      />
                    </motion.div>

                    {/* Shopify */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.8, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/shopify-icon.svg" 
                        alt="Shopify" 
                        className="w-8 h-8"
                      />
                    </motion.div>

                    {/* Wix */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.0, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/wix-icon.svg" 
                        alt="Wix" 
                        className="w-8 h-8"
                      />
                    </motion.div>

                    {/* Webhook */}
                    <motion.div
                      className="flex items-center justify-center p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.2, duration: 0.4, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <img 
                        src="/images/integration-logos/webhook-icon.svg" 
                        alt="Webhook" 
                        className="w-8 h-8"
                      />
                    </motion.div>
                  </div>

                  {/* Success Message */}
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
                  >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
                      <motion.div
                        className="w-2 h-2 bg-green-500 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                      <span className="text-xs font-semibold text-green-700">
                        Published Successfully
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Publish automatically
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Articles are automatically published to your WordPress, Webflow, Notion, Shopify, Wix, and more. Your blog grows on autopilot while you focus on your business.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutomatedContentPipeline;

